// 捨牌反應的優先序決策（純函式）
// 從 CRound.resolveReactions 抽出「誰的反應勝出」這段判斷，與「套用副露 / 補牌 / 推進」的
// 狀態變化分離。純函式：只讀 reactions 與各家 hands（判 melds 上限），回傳「下一步該做什麼」，
// 實際套用仍由 CRound 負責（補牌、emit、推進 turn）。
//
// 優先序：胡（順位攔胡後只會有一位） > 碰/明槓 > 吃 > 全過。

import { PlayerActionType, type TPlayerAction } from './actions';
import type { THand } from './hand';
import type { TChiOption, TKongExposedOption, TPongOption } from './ruleset';
import type { TSeat } from './types';

/** 反應解析結果：CRound 依此套用對應的狀態變化。 */
export type TReactionResolution =
  | { readonly kind: 'hu'; readonly seat: TSeat }
  | { readonly kind: 'pong'; readonly seat: TSeat; readonly option: TPongOption }
  | { readonly kind: 'kong-exposed'; readonly seat: TSeat; readonly option: TKongExposedOption }
  | { readonly kind: 'chi'; readonly seat: TSeat; readonly option: TChiOption }
  | { readonly kind: 'pass' };

/**
 * 依優先序決定收到的反應由誰勝出。
 * 已達 5 個面子上限的家，其吃/碰/槓反應視為 pass（結構上無法再胡）。
 * 不變動任何狀態；reactions 的 Map 插入序即同優先層內的 tie-break 序（與舊實作一致）。
 */
export const resolveReactionPriority = (
  reactions: ReadonlyMap<TSeat, TPlayerAction>,
  hands: readonly THand[]
): TReactionResolution => {
  // 1. 胡；CRound 會先依順位逐家詢問，因此第一筆就是唯一成立者。
  for (const [s, a] of reactions.entries()) {
    if (a.type === PlayerActionType.Hu) return { kind: 'hu', seat: s };
  }

  // 2. 碰 / 明槓（互斥於吃）— 已達面子上限者跳過
  for (const [s, a] of reactions.entries()) {
    if (hands[s]!.melds.length >= 5) continue;
    if (a.type === PlayerActionType.Pong) return { kind: 'pong', seat: s, option: a.option };
    if (a.type === PlayerActionType.KongExposed) {
      return { kind: 'kong-exposed', seat: s, option: a.option };
    }
  }

  // 3. 吃
  for (const [s, a] of reactions.entries()) {
    if (hands[s]!.melds.length >= 5) continue;
    if (a.type === PlayerActionType.Chi) return { kind: 'chi', seat: s, option: a.option };
  }

  // 4. 全過
  return { kind: 'pass' };
};
